import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { FileDown, Lock, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { DisplayCurrency } from "@/hooks/finance/cash-flow/useCashFlow";
import { usePublicInvestorReport } from "@/hooks/finance/usePublicInvestorReport";
import {
  deserializeCashFlowRows,
  deserializeBSRows,
  deserializeMetaMap,
} from "@/components/finance/reports/shared/snapshotSerialize";
import { exportCombinedInvestorPdf } from "@/components/finance/reports/shared/exportCombinedInvestorPdf";
import {
  exportCombinedInvestorExcel,
  type CurrencyReportBundle,
} from "@/components/finance/reports/shared/exportCombinedInvestorExcel";

import { FinancialReportTable } from "@/components/finance/reports/financial-report/FinancialReportTable";
import { ProfitAndLossTable } from "@/components/finance/reports/pnl-report/ProfitAndLossTable";
import { BalanceSheetTable } from "@/components/finance/reports/balance-sheet/BalanceSheetTable";

function FullScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen w-full bg-slate-100 flex items-center justify-center p-6">
      <div className="text-center space-y-3">{children}</div>
    </div>
  );
}

export default function PublicInvestorReportPage() {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading, error } = usePublicInvestorReport(token);
  const [currency, setCurrency] = useState<DisplayCurrency>("ETB");

  const bundle = data?.payload?.reports?.[currency];

  // Rehydrate Maps for the selected currency (memoized so table renders/exports
  // are stable while toggling).
  const view = useMemo(() => {
    if (!bundle) return null;
    return {
      cashflow: {
        rows: deserializeCashFlowRows(bundle.cashflow.rows),
        periodLabels: bundle.cashflow.periodLabels,
        periodKeys: bundle.cashflow.periodKeys,
        openingMeta: deserializeMetaMap(bundle.cashflow.openingMeta),
        closingMeta: deserializeMetaMap(bundle.cashflow.closingMeta),
      },
      pnl: {
        rows: deserializeCashFlowRows(bundle.pnl.rows),
        periodLabels: bundle.pnl.periodLabels,
        periodKeys: bundle.pnl.periodKeys,
      },
      balanceSheet: {
        rows: deserializeBSRows(bundle.balanceSheet.rows),
        periods: bundle.balanceSheet.periods,
      },
    };
  }, [bundle]);

  if (isLoading) {
    return (
      <FullScreen>
        <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto" />
        <p className="text-sm text-slate-500">Loading report…</p>
      </FullScreen>
    );
  }

  if (error || !data || !view) {
    return (
      <FullScreen>
        <Lock className="h-8 w-8 text-slate-400 mx-auto" />
        <p className="text-base font-semibold text-slate-700">Report unavailable</p>
        <p className="text-sm text-slate-500 max-w-sm">
          This investor link is invalid or has been revoked. Please request a new
          link from ChipChip.
        </p>
      </FullScreen>
    );
  }

  const availableCurrencies = (data.currencies?.length
    ? data.currencies
    : ["ETB"]) as DisplayCurrency[];

  // Rehydrate a currency's bundle from the snapshot payload (the combined
  // downloads need both currencies, whereas `view` above only covers the
  // selected one).
  const rehydrateBundle = (cur: DisplayCurrency): CurrencyReportBundle | null => {
    const b = data.payload?.reports?.[cur];
    if (!b) return null;
    return {
      cashflow: {
        rows: deserializeCashFlowRows(b.cashflow.rows),
        periodLabels: b.cashflow.periodLabels,
        periodKeys: b.cashflow.periodKeys,
      },
      pnl: {
        rows: deserializeCashFlowRows(b.pnl.rows),
        periodLabels: b.pnl.periodLabels,
        periodKeys: b.pnl.periodKeys,
      },
      balanceSheet: {
        rows: deserializeBSRows(b.balanceSheet.rows),
        periods: b.balanceSheet.periods,
      },
    };
  };

  const hasBothCurrencies =
    !!data.payload?.reports?.ETB && !!data.payload?.reports?.USD;

  const downloadCombinedExcel = () => {
    const etb = rehydrateBundle("ETB");
    const usd = rehydrateBundle("USD");
    if (!etb || !usd) return;
    exportCombinedInvestorExcel({
      etb,
      usd,
      fileName: `chipchip-financials-ETB-USD-${format(new Date(), "yyyyMMdd")}.xlsx`,
    });
  };

  const downloadCombinedPdf = () => {
    const etb = rehydrateBundle("ETB");
    const usd = rehydrateBundle("USD");
    if (!etb || !usd) return;
    exportCombinedInvestorPdf({
      etb,
      usd,
      fileName: `chipchip-financials-ETB-USD-${format(new Date(), "yyyyMMdd")}.pdf`,
    });
  };

  return (
    <div className="min-h-screen w-full bg-slate-100">
      {/* Read-only header — deliberately no date/filter controls */}
      <header className="bg-slate-900 text-white">
        <div className="mx-auto max-w-[1400px] px-6 py-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-extrabold tracking-tight">
              {data.title ?? "ChipChip Financials"}
            </h1>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {data.dateFrom} → {data.dateTo} · Read-only snapshot
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5 rounded-md bg-slate-800 p-0.5">
              {availableCurrencies.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCurrency(c)}
                  className={
                    "px-3 py-1 rounded text-[10px] font-bold transition-all " +
                    (currency === c
                      ? "bg-white text-slate-900"
                      : "text-slate-300 hover:text-white")
                  }
                >
                  {c}
                </button>
              ))}
            </div>
            {hasBothCurrencies && (
              <Button
                size="sm"
                variant="secondary"
                className="h-8 text-xs gap-1.5"
                onClick={downloadCombinedPdf}
                title="Download all three reports, both currencies, as one PDF"
              >
                <FileDown className="h-3.5 w-3.5" /> PDF (ETB + USD)
              </Button>
            )}
            {hasBothCurrencies && (
              <Button
                size="sm"
                variant="secondary"
                className="h-8 text-xs gap-1.5"
                onClick={downloadCombinedExcel}
                title="Download all three reports for both currencies as one Excel workbook"
              >
                <FileDown className="h-3.5 w-3.5" /> Excel (ETB + USD)
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 py-6 space-y-6">
        <FinancialReportTable
          rows={view.cashflow.rows}
          periodLabels={view.cashflow.periodLabels}
          periodKeys={view.cashflow.periodKeys}
          isLoading={false}
          displayCurrency={currency}
          openingMetaByPeriod={view.cashflow.openingMeta}
          closingMetaByPeriod={view.cashflow.closingMeta}
          dateFrom={data.dateFrom ?? undefined}
          dateTo={data.dateTo ?? undefined}
        />
        <ProfitAndLossTable
          rows={view.pnl.rows}
          periodLabels={view.pnl.periodLabels}
          periodKeys={view.pnl.periodKeys}
          isLoading={false}
          displayCurrency={currency}
        />
        <BalanceSheetTable
          rows={view.balanceSheet.rows}
          periods={view.balanceSheet.periods}
          isLoading={false}
          displayCurrency={currency}
        />

        <p className="text-center text-[11px] text-slate-400 pb-8">
          This is a point-in-time snapshot shared by ChipChip.
        </p>
      </main>
    </div>
  );
}
